import type { FacilityBenefits } from '@/types/FacilityBenefits';
import type { StaticListings } from '@/types/StaticListings';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { MinusCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import type { PropertySubTypes } from '@/types/PropertySubTypes';
import type { PropertyTypes } from '@/types/PropertyTypes';
import Helper from '@/lib/helper';
import LabelWithAsterisk from '@/components/LabelWithAsterisk';

interface _PropertyDetails {
    propertyTypes: PropertyTypes[];
    propertySubTypes: PropertySubTypes[] | null;
    propertyUnitTypes: StaticListings[];
    tenures: StaticListings[];
    titleTypes: StaticListings[];
    directions: StaticListings[];
    zoning: StaticListings[];
    yearOfCompletion: string;
    setYearOfCompletion: React.Dispatch<React.SetStateAction<string>>;
    facilityBenefits: FacilityBenefits[] | null;
    selectedFacilityBenefits: number[];
    selectedProjectType: number | null;
    setSelectedFacilityBenefits: React.Dispatch<React.SetStateAction<number[]>>;
    selectedDirection: number | null;
    setSelectedDirection: React.Dispatch<React.SetStateAction<number | null>>;
    selectedTenure: number | null;
    setSelectedTenure: React.Dispatch<React.SetStateAction<number | null>>;
    selectedTitleType: number | null;
    setSelectedTitleType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedPropertyType: number | null;
    setSelectedPropertyType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedPropertySubType: number | null;
    setSelectedPropertySubType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedPropertyUnitType: number | null;
    setSelectedPropertyUnitType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedLandTypeTitle: number | null;
    setSelectedLandTypeTitle: React.Dispatch<React.SetStateAction<number | null>>;
    selectedZoning: number | null;
    setSelectedZoning: React.Dispatch<React.SetStateAction<number | null>>;
    isBumiLot: number | null;
    setIsBumiLot: React.Dispatch<React.SetStateAction<number | null>>;
    landTypeTitles: StaticListings[];
    markAsDuplicate?: boolean;
    edit?: boolean;
}

export interface PropertyDetailsRef {
    callback: () => boolean;
}

const PropertyDetails = forwardRef<PropertyDetailsRef, _PropertyDetails>(({
    propertyTypes,
    propertySubTypes,
    propertyUnitTypes,
    tenures,
    titleTypes,
    directions,
    zoning,
    selectedDirection,
    setSelectedDirection,
    selectedTenure,
    setSelectedTenure,
    selectedTitleType,
    selectedProjectType,
    setSelectedTitleType,
    selectedPropertyType,
    setSelectedPropertyType,
    selectedPropertySubType,
    setSelectedPropertySubType,
    selectedPropertyUnitType,
    setSelectedPropertyUnitType,
    selectedZoning,
    setSelectedZoning,
    yearOfCompletion,
    setYearOfCompletion,
    facilityBenefits,
    selectedFacilityBenefits,
    setSelectedFacilityBenefits,
    selectedLandTypeTitle,
    setSelectedLandTypeTitle,
    isBumiLot,
    setIsBumiLot,
    landTypeTitles,
    markAsDuplicate,
    edit
}, ref) => {
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const removeError = (field: string) => {
        setValidationErrors(prevErrors => prevErrors.filter(x => x !== field));
    };

    const callback = () => {
        try {
            let valErrors = [];
            if(!selectedPropertyType) {
                valErrors.push('propertyType');
            }

            if(!selectedPropertySubType) {
                valErrors.push('propertySubType');
            }

            // if(selectedPropertySubType && 
            //     ![4, 18, 29, 30, 32, 38, 39].includes(selectedPropertySubType) &&
            //     !selectedPropertyUnitType) {
            //     valErrors.push('propertyUnitType');
            // }

            if(selectedProjectType === 1 && !yearOfCompletion) {
                valErrors.push('yearOfCompletion');
            }

            if(!selectedTenure) {
                valErrors.push('tenure');
            }

            if(!selectedTitleType) {
                valErrors.push('titleType');
            }

            setValidationErrors(valErrors);
            if(valErrors.length > 0) {
                throw new Error('Please complete all required fields.');
            }
            return true;
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            return false;
        }
    }

    useImperativeHandle(ref, () => ({
        callback
    }));

    return (
        <section>
            <div className="mt-3 pt-5">
                <LabelWithAsterisk label='Property type' className='text-black'/>
                <div>
                    {
                        propertyTypes.map((type) => (
                            <Button
                            key={type.id}
                            size={'sm'}
                            className={`mx-1 cursor-pointer ${validationErrors.includes('propertyType') ? 'border-red-400' : ''} mt-3 ${selectedPropertyType !== type.id && 'text-black'}`}
                            variant={selectedPropertyType === type.id ? 'default' : 'outline'}
                            onClick={(e) => { 
                                e.preventDefault(); 
                                setSelectedPropertyType(type.id);
                                setSelectedPropertySubType(null);
                                removeError('propertyType');
                            }}
                            disabled={markAsDuplicate || edit}>
                                { type.name }
                            </Button>
                        ))
                    }
                </div>
                {
                    validationErrors.includes('propertyType') && (
                        <span className='text-xs text-red-400'>This field is required</span>
                    )
                }
            </div>
            {
                propertySubTypes && (
                    <div className="mt-3 pt-5">
                        <LabelWithAsterisk label='Property sub type' className='text-black'/>
                        <div>
                        {
                            propertySubTypes.map((type) => (
                                <Button
                                key={type.id}
                                size={'sm'}
                                className={`mx-1 cursor-pointer mt-3 ${validationErrors.includes('propertySubType') && 'border-red-400'} ${selectedPropertySubType !== type.id && 'text-black'}`}
                                variant={selectedPropertySubType === type.id ? 'default' : 'outline'}
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    setSelectedPropertySubType(type.id) ;
                                    removeError('propertySubType');
                                }}
                                disabled={markAsDuplicate || edit}>
                                    { type.name }
                                </Button>
                            ))
                        }
                        </div>
                        {
                            validationErrors.includes('propertySubType') && (
                                <span className='text-red-400 text-xs'>Property sub-type is required.</span>
                            )
                        }
                    </div>
                )
            }
            {
                (selectedPropertySubType == null || ![4, 18, 29, 30, 32, 38, 39].includes(selectedPropertySubType)) && (
                    <div className="mt-3 pt-5">
                        <Label className='text-black'>Property unit type</Label>
                        <div>
                        {
                            propertyUnitTypes
                            .filter((_, index) => {
                                if (selectedPropertyType === 6 || selectedPropertyType === 7) {
                                    return index <= 3;
                                }
                                return true;
                            })
                            .map((type) => (
                                    <Button
                                    key={type.value}
                                    size="sm"
                                    className={`mx-1 cursor-pointer mt-3 ${
                                    validationErrors.includes('propertyUnitType') && 'border-red-400'
                                    } ${
                                    selectedPropertyUnitType !== type.value && 'text-black'
                                    }`}
                                    variant={selectedPropertyUnitType === type.value ? 'default' : 'outline'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setSelectedPropertyUnitType(type.value);
                                        removeError('propertyUnitType');
                                    }}
                                    disabled={markAsDuplicate || edit}>
                                        {type.label}
                                    </Button>
                                ))
                            }
                        </div>
                        {/* {
                            validationErrors.includes('propertyUnitType') && (
                                <span className='text-xs text-red-400'>Property unit-type is required.</span>
                            )
                        } */}
                    </div>
                )
            }
            <div className="mt-3 pt-5">
                <LabelWithAsterisk label='Tenure ' className='text-black'/>
                <div>
                    {
                        tenures.map((tenure) => (
                            <Button
                            key={tenure.value}
                            size={'sm'}
                            className={`mx-1 cursor-pointer mt-3 ${validationErrors.includes('tenure') && 'border-red-400'} ${selectedTenure !== tenure.value && 'text-black'}`}
                            variant={selectedTenure === tenure.value ? 'default' : 'outline'}
                            onClick={(e) => { 
                                e.preventDefault(); 
                                setSelectedTenure((tenure.value));
                                removeError('tenure');
                            }}
                            disabled={markAsDuplicate || edit}>
                                {tenure.label}
                            </Button>
                        ))
                    }
                </div>
                {
                    validationErrors.includes('tenure') && (
                        <span className='text-red-400 text-xs'>Tenure is required.</span>
                    )
                }
            </div>
            <div className="mt-3 pt-5">
                {
                    selectedProjectType === 1 ? 
                    <LabelWithAsterisk label='Completion year' className='text-black'/>
                    :
                    <Label className="text-black flex">Completion year</Label>
                }
                <Input 
                className={`md:w-1/2 mt-3 ${validationErrors.includes('yearOfCompletion') && 'border-red-500'}`}
                value={yearOfCompletion}
                onKeyDown={Helper.allowOnlyNumbers}
                onChange={(e) => {
                    const value = e.target.value;
                    setYearOfCompletion(value);
                    removeError('yearOfCompletion');
                }}
                disabled={markAsDuplicate || edit}/>
                {
                    validationErrors.includes('yearOfCompletion') && (
                        <span className='text-xs text-red-400'>Completion year is required.</span>
                    )
                }
            </div>
            <div className="mt-3 pt-5">
                <LabelWithAsterisk label='Title type' className='text-black'/>
                <div className="mt-3">
                    {
                        titleTypes.map((title) => (
                            <Button
                            key={title.value}
                            size={'sm'}
                            className={`mx-1 cursor-pointer ${validationErrors.includes('titleType') && 'border-red-400'} ${selectedTitleType !== title.value && 'text-black'}`}
                            variant={selectedTitleType === title.value ? 'default' : 'outline'}
                            onClick={(e) => { 
                                e.preventDefault(); 
                                setSelectedTitleType(title.value);
                                removeError('titleType');
                            }}
                            disabled={markAsDuplicate || edit}>
                                {title.label}
                            </Button>
                        ))
                    }
                </div>
                {
                    validationErrors.includes('titleType') && (
                        <span className='text-xs text-red-400'>Title type is required.</span>
                    )
                }
            </div>
            <div className="mt-3 pt-5">
                <Label className="text-black">Is Bumi Lot?</Label>
                <div>
                    <Button
                    size={'sm'}
                    className={`mx-1 cursor-pointer mt-3 ${isBumiLot !== 1 && 'text-black'}`}
                    variant={isBumiLot === 1 ? 'default' : 'outline'}
                    onClick={(e) => { 
                        e.preventDefault(); 
                        setIsBumiLot(1);
                    }}
                    disabled={markAsDuplicate || edit}>
                        Yes
                    </Button>
                    <Button
                    size={'sm'}
                    className={`mx-1 cursor-pointer mt-3 ${isBumiLot !== 2 && 'text-black'}`}
                    variant={isBumiLot === 2 ? 'default' : 'outline'}
                    onClick={(e) => { 
                        e.preventDefault(); 
                        setIsBumiLot(2);
                    }}
                    disabled={markAsDuplicate || edit}>
                        No
                    </Button>
                </div>
            </div>
            {
                selectedPropertyType === 7 && (
                    <div className="mt-3 pt-5">
                        <Label className="text-black">Land Title Type</Label>
                        <div>
                            {
                                landTypeTitles.map((type) => (
                                    <Button
                                    key={type.value}
                                    size={'sm'}
                                    className={`mx-1 cursor-pointer mt-3 ${selectedLandTypeTitle !== type.value && 'text-black'}`}
                                    variant={selectedLandTypeTitle === type.value ? 'default' : 'outline'}
                                    onClick={(e) => { 
                                        e.preventDefault(); 
                                        setSelectedLandTypeTitle(type.value) 
                                    }}
                                    disabled={markAsDuplicate || edit}>
                                        {type.label}
                                    </Button>
                                ))
                            }
                        </div>
                    </div>
                )
            }
            <div className="mt-3 pt-5">
                <Label>Zoning</Label>
                <div className="mt-3">
                {
                    zoning.map((zone) => (
                        <Button
                        key={zone.value}
                        size={'sm'}
                        className={`mx-1 cursor-pointer ${validationErrors.includes('zoning') && 'border-red-400'} ${selectedZoning !== zone.value && 'text-black'}`}
                        variant={selectedZoning === zone.value ? 'default' : 'outline'}
                        onClick={(e) => { 
                            e.preventDefault(); 
                            setSelectedZoning(zone.value);
                        }}
                        disabled={markAsDuplicate || edit}>
                            {zone.label}
                        </Button>
                    ))
                }
                </div>
            </div>
            <div className="mt-3 pt-5">
                <Label className="text-black">Direction</Label>
                <div>
                    {
                        directions.map((direction) => (
                            <Button
                            key={direction.value}
                            size={'sm'}
                            className={`mx-1 cursor-pointer mt-3 ${selectedDirection !== direction.value && 'text-black'}`}
                            variant={selectedDirection === direction.value ? 'default' : 'outline'}
                            onClick={(e) => { 
                                e.preventDefault(); 
                                setSelectedDirection((direction.value)) }
                            }
                            disabled={markAsDuplicate || edit}>
                                {direction.label}
                            </Button>
                        ))
                    }
                </div>
            </div>
            <hr className='my-5'/>
            <div className="mt-3 pt-5">
                <Label className="text-black">Facility Benefits</Label>
                <div className="flex flex-wrap gap-2 mt-3">
                    {
                        selectedFacilityBenefits.length > 0 ? (
                            facilityBenefits?.filter((b) => selectedFacilityBenefits.includes(b.id))
                                .map((benefit) => (
                                    <Button 
                                    type='button'
                                    key={benefit.id} 
                                    size="sm" 
                                    variant="outline"

                                    onClick={(e) => {
                                        setSelectedFacilityBenefits((prev) =>
                                            prev.filter((id) => id !== benefit.id)
                                        );
                                    }}
                                    disabled={markAsDuplicate || edit}>
                                        {benefit.name}
                                        <MinusCircle 
                                        className='text-red-400 cursor-pointer'/>
                                    </Button>
                                ))
                            ) : (
                            <span className="text-sm text-muted-foreground">No benefits selected</span>
                        )
                    }
                </div>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button 
                        size="sm" 
                        variant="secondary" 
                        className='mt-3' 
                        disabled={markAsDuplicate || edit}>
                        Select Benefits
                        </Button>
                    </DialogTrigger>
                    <DialogContent className='max-h-[90vh] overflow-y-auto'>
                        <DialogHeader>
                            <DialogTitle>Select Facility Benefits</DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-3 mt-4">
                        {
                            facilityBenefits && facilityBenefits.map((benefit) => (
                                <div key={benefit.id} className="flex items-center space-x-2">
                                    <Checkbox
                                    id={`benefit-${benefit.id}`}
                                    checked={selectedFacilityBenefits.includes(benefit.id)}
                                    onCheckedChange={(checked) => {
                                        if (checked) {
                                            setSelectedFacilityBenefits((prev) => [...prev, benefit.id]);
                                        } else {
                                            setSelectedFacilityBenefits((prev) =>
                                                prev.filter((id) => id !== benefit.id)
                                            );
                                        }
                                    }}
                                    disabled={markAsDuplicate || edit}/>
                                    <label
                                    htmlFor={`benefit-${benefit.id}`}
                                    className="text-sm font-medium leading-none">
                                        {benefit.name}
                                    </label>
                                </div>
                            ))
                        }
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </section>
    )
})

export default PropertyDetails